import throttle from "lodash/throttle";
import sortBy from "lodash/sortBy";

$(() => {
  const reportContent = document.getElementById("report-content");
  if (reportContent === null || reportContent === undefined) return;
  const tocLinksContainer = document.getElementById("toc-links");
  const headerTagsToIncludeInToc = "h1, h2, h3";
  const headerTagsInReportContent = reportContent.querySelectorAll(headerTagsToIncludeInToc);
  let tocLinks = [];

  createToc(tocLinks, headerTagsInReportContent, tocLinksContainer);
  deepLinkScroll();
  window.addEventListener("hashchange", deepLinkScroll);
  makeH3LinksExpandable(tocLinks, tocLinksContainer);
  addLinkHighlighting(headerTagsInReportContent, tocLinks);
  makeTocToggleableForSmallScreens();
  addInitialHighlight(tocLinks);
  addKeyboardNavigation(tocLinksContainer);
});

const createSlugFromText = (text, usedSlugs = {}) => {
  // Create a temporary element to extract text content
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = text;
  const plainText = tempDiv.textContent || tempDiv.innerText || "";

  // Convert to lowercase and trim
  let slug = plainText.trim().toLowerCase();

  // Replace Norwegian characters
  slug = slug
    .replace(/æ/g, "ae")
    .replace(/ø/g, "o")
    .replace(/å/g, "aa")
    .replace(/Æ/g, "ae")
    .replace(/Ø/g, "o")
    .replace(/Å/g, "aa");

  // Replace spaces and underscores with hyphens
  slug = slug.replace(/[\s_]+/g, "-");

  // Remove special characters, keep only alphanumeric and hyphens
  slug = slug.replace(/[^a-z0-9-]/g, "");

  // Remove multiple consecutive hyphens
  slug = slug.replace(/-+/g, "-");

  // Remove leading and trailing hyphens
  slug = slug.replace(/^-+|-+$/g, "");

  if (!slug) {
    slug = "section";
  }

  // Ensure uniqueness by appending a number if needed
  let uniqueSlug = slug;
  let counter = 1;
  while (usedSlugs[uniqueSlug]) {
    uniqueSlug = `${slug}-${counter}`;
    counter++;
  }
  usedSlugs[uniqueSlug] = true;

  return uniqueSlug;
};

/**
 * Loop through all header tags in the report, as defined by @const headerTagsToIncludeInToc,
 * and creates and appends an anchor-tag for each header in the toc.
 */
const createToc = (tocLinks, headerTagsInReportContent, tocLinksContainer) => {
  let currentH2Id = "";
  const usedSlugs = {}; // Track used slugs to ensure uniqueness

  for (let i = 0; i < headerTagsInReportContent.length; i++) {
    const tocLink = document.createElement("a");
    const tocLinkWrapper = document.createElement("div");
    const currentHeaderTag = headerTagsInReportContent[i];

    // Skip headers with className set, to only include normal headers created through TinyMCE
    if (currentHeaderTag.className !== "") {
      continue;
    }

    // Generate slug from header text
    const slug = createSlugFromText(currentHeaderTag.innerHTML, usedSlugs);
    let headerId = slug;
    let tocLinkId = headerId;

    tocLink.textContent = currentHeaderTag.textContent;
    tocLink.className = "report-toc--link";
    tocLinkWrapper.className = "report-toc--link-wrapper";

    switch (currentHeaderTag.tagName) {
      case "H1":
        tocLink.className += " report-toc--link--h1";
        break;
      case "H2":
        tocLink.className += " report-toc--link--h2";
        currentH2Id = headerId;
        break;
      case "H3":
        tocLink.className += " report-toc--link--h3--hidden";
        tocLink.setAttribute("data-parent-h2", currentH2Id);
        // Initialize with H2 ID and H3 slug
        let uniqueCombinedId = `${currentH2Id}#${slug}`;
        let counter = 1;
        // Ensure uniqueness for the combined ID
        while (usedSlugs[uniqueCombinedId]) {
          uniqueCombinedId = `${currentH2Id}#${slug}-${counter}`;
          counter++;
        }
        usedSlugs[uniqueCombinedId] = true;
        headerId = uniqueCombinedId;
        // TOC link ID includes parent H2 for grouping
        tocLinkId = headerId + ":" + currentH2Id;
        break;
      default:
        tocLink.className = "report-toc--link";
        break;
    }

    currentHeaderTag.id = headerId;
    tocLink.href = "#" + headerId;
    tocLink.id = "toc-" + tocLinkId;

    tocLinkWrapper.appendChild(tocLink);
    tocLinks.push(tocLink);
    tocLinksContainer.appendChild(tocLinkWrapper);
  }
};

const deepLinkScroll = () => {
  if (!location.hash) return;
  const id = location.hash.slice(1);
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ block: "start", behavior: "auto" });
    }
  });
};

/**
 * Loop through all links in the TOC. If there are any links to h2-titles which has sub-links to h3-titles,
 * add a toggleable arrow-icon and onClick event which toggles the corresponding h3-titles in the TOC.
 * Implements true accordion behaviour: opening one H2 collapses any previously open H2.
 *
 * Uses a Map from h2Link → { h3Links, arrowIcon, expanded } so that expand/collapse state is
 * tracked explicitly and h3 children are looked up directly — no fragile className-string probing
 * or DOM traversal with firstChild in the hot path.
 */
const makeH3LinksExpandable = (tocLinks, tocLinksContainer) => {
  const visibleCls = "report-toc--link report-toc--link--h3--visible";
  const hiddenCls = "report-toc--link report-toc--link--h3--hidden";
  const downArrowCls =
    "report-toc--expand report-toc--expand--down report-toc--expand--down--desktop";
  const upArrowCls = "report-toc--expand report-toc--expand--up report-toc--expand--up--desktop";

  // Build map: h2Link element → { h3Links[], arrowIcon, expanded }
  // Uses the data-parent-h2 attribute set on each h3 link during createToc for exact matching.
  const h2Groups = new Map();
  tocLinks.forEach((link) => {
    if (!link.className.includes("--h2")) return;
    const h2Id = getIdFromTocId(link.id);
    const h3Links = tocLinks.filter((l) => l.getAttribute("data-parent-h2") === h2Id);
    if (h3Links.length > 0) {
      h2Groups.set(link, { h3Links, arrowIcon: null, expanded: false });
    }
  });

  const collapseGroup = (h2Link) => {
    const group = h2Groups.get(h2Link);
    if (!group || !group.expanded) return;
    group.h3Links.forEach((l) => (l.className = hiddenCls));
    if (group.arrowIcon) {
      group.arrowIcon.className = downArrowCls;
      group.arrowIcon.setAttribute("aria-expanded", "false");
    }
    group.expanded = false;
  };

  const expandGroup = (h2Link) => {
    const group = h2Groups.get(h2Link);
    if (!group) return;
    // Collapse all other open groups first (accordion)
    h2Groups.forEach((_, otherH2) => {
      if (otherH2 !== h2Link) collapseGroup(otherH2);
    });
    group.h3Links.forEach((l) => (l.className = visibleCls));
    if (group.arrowIcon) {
      group.arrowIcon.className = upArrowCls;
      group.arrowIcon.setAttribute("aria-expanded", "true");
    }
    group.expanded = true;
  };

  tocLinks.forEach((link) => {
    const isExpandableH2 = h2Groups.has(link);

    // Non-expandable links close the TOC on mobile when clicked
    if (!isExpandableH2) {
      $(link).on("click", () => $("#toc-links").toggle());
      return;
    }

    const group = h2Groups.get(link);
    const h2Id = getIdFromTocId(link.id);
    const btnId = `open-arrow-${h2Id}`;

    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.id = btnId;
    toggleBtn.className = downArrowCls;
    toggleBtn.setAttribute("aria-expanded", "false");
    toggleBtn.setAttribute("aria-labelledby", `${btnId} ${link.id}`);

    const srText = document.createElement("span");
    srText.className = "sr-only";
    srText.textContent = "Underoverskrifter til";
    toggleBtn.appendChild(srText);

    link.after(toggleBtn);
    group.arrowIcon = toggleBtn;

    toggleBtn.addEventListener("click", () => {
      if (group.expanded) {
        collapseGroup(link);
      } else {
        expandGroup(link);
      }
    });

    toggleBtn.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault(); // prevent native button click (avoids double-toggle) + page scroll for Space
        e.stopPropagation(); // prevent addKeyboardNavigation from also handling Enter
        if (group.expanded) {
          collapseGroup(link);
        } else {
          expandGroup(link);
        }
      }
    });
  });
};

/**
 * Create an onScroll event which is throttled by lodash's 'throttle'-package,
 * at an interval set by @const delayForOnScrollEventInMs.
 * The function collects all header tags in the report, finds the one which is
 * located closest to the top of the user's viewport, and then highlights the
 * matching link in the toc.
 */
const addLinkHighlighting = (headerTagsInReportContent, tocLinks) => {
  const delayForOnScrollEventInMs = 300;

  $(window).on(
    "scroll",
    throttle(() => {
      const headerTagsArray = Array.from(headerTagsInReportContent);

      const headerTagClosestToTopOfViewport = sortBy(headerTagsArray, (tag) =>
        Math.abs(tag.getBoundingClientRect().top - 0)
      )[0];

      // Extract hash from href (handles both relative "#id" and absolute "url#id")
      const getHashFromHref = (href) => {
        const hashIndex = href.indexOf("#");
        return hashIndex >= 0 ? href.substring(hashIndex + 1) : "";
      };

      let chapterLinkToHighlight = tocLinks.find(
        (link) => getHashFromHref(link.href) === headerTagClosestToTopOfViewport.id
      );

      if (chapterLinkToHighlight) {
        // If a toc-link which is hidden should be highlighted, highlight it's parent toc-link instead.
        if (chapterLinkToHighlight.className.includes("hidden")) {
          chapterLinkToHighlight = tocLinks.find(
            (link) => getHashFromHref(link.href) === headerTagClosestToTopOfViewport.id
          );
        }

        tocLinks.forEach((link) => link.classList.remove("current-link"));
        chapterLinkToHighlight.classList.add("current-link");
      }
    }, delayForOnScrollEventInMs)
  );
};

/**
 * Creates toggle behaviour for the toc on 'md' and lower media sizes.
 * The mobile button manages aria-expanded and arrow direction classes.
 * Clicking the h2 title text also toggles, but the button handles its
 * own click without propagating to the container (avoids double-toggle).
 */
const makeTocToggleableForSmallScreens = () => {
  const $toggleBtn = $("#report-toc--expand--mobile");
  const $tocLinks = $("#toc-links");

  const toggle = () => {
    $tocLinks.toggle();
    const nowVisible = $tocLinks.is(":visible");
    $toggleBtn.attr("aria-expanded", nowVisible ? "true" : "false");
    $toggleBtn
      .toggleClass("report-toc--expand--down", !nowVisible)
      .toggleClass("report-toc--expand--up", nowVisible);
  };

  $("#report-toc--title-container").on("click", (e) => {
    if ($(e.target).closest("#report-toc--expand--mobile").length) return;
    toggle();
  });

  $toggleBtn.on("click", (e) => {
    e.stopPropagation();
    toggle();
  });

  $toggleBtn.on("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); // prevent native button click (avoids double-toggle) + page scroll for Space
      toggle();
    }
  });
};

const addInitialHighlight = (tocLinks) => {
  if (tocLinks[0]) tocLinks[0].classList.add("current-link");
};

/**
 * Adds keyboard navigation to the TOC.
 *
 * Arrow keys (Up/Down):   Move focus through visible TOC links AND toggle buttons.
 * Home / End:             Jump to the first / last visible item.
 * ArrowRight:             Expand a collapsed H2 accordion section.
 * ArrowLeft:              Collapse an expanded H2 accordion section.
 * Enter / Space:          On a toggle button — open/close the accordion.
 *                         On a heading link — navigate to the section (native behaviour).
 */
const addKeyboardNavigation = (tocLinksContainer) => {
  // Returns all focusable TOC items that are currently visible:
  // visible <a> links AND their paired toggle <button>s.
  const getVisibleItems = () => {
    const items = [];
    [...tocLinksContainer.querySelectorAll(".report-toc--link-wrapper")].forEach((wrapper) => {
      const link = wrapper.querySelector("a.report-toc--link");
      const btn = wrapper.querySelector("button.report-toc--expand");
      if (link && !link.className.includes("h3--hidden")) {
        items.push(link);
        if (btn) items.push(btn);
      }
    });
    return items;
  };

  const isToggleButton = (el) =>
    el.tagName === "BUTTON" && el.classList.contains("report-toc--expand");

  const isExpandableLink = (el) => {
    const sibling = el.nextElementSibling;
    return sibling !== null && isToggleButton(sibling);
  };

  tocLinksContainer.addEventListener("keydown", (e) => {
    const focused = document.activeElement;

    const isTocLink = focused && focused.classList.contains("report-toc--link");
    const isBtn = focused && isToggleButton(focused);
    if (!isTocLink && !isBtn) return;

    const visibleItems = getVisibleItems();
    const currentIndex = visibleItems.indexOf(focused);
    if (currentIndex === -1) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < visibleItems.length - 1) {
          visibleItems[currentIndex + 1].focus();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          visibleItems[currentIndex - 1].focus();
        }
        break;

      case "Home":
        e.preventDefault();
        visibleItems[0].focus();
        break;

      case "End":
        e.preventDefault();
        visibleItems[visibleItems.length - 1].focus();
        break;

      case "Enter":
        if (isBtn) {
          e.preventDefault();
          focused.click();
        } else if (isTocLink) {
          // Scroll to the target section and update the URL, but keep focus
          // in the TOC so the user can continue Tab-navigating the list.
          e.preventDefault();
          const href = focused.getAttribute("href");
          if (href && href.startsWith("#")) {
            const target = document.getElementById(href.slice(1));
            if (target) {
              history.pushState(null, "", href);
              target.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        }
        break;

      case "ArrowRight":
        if (isTocLink && isExpandableLink(focused)) {
          const btn = focused.nextElementSibling;
          if (btn.getAttribute("aria-expanded") === "false") {
            e.preventDefault();
            btn.click();
          }
        }
        break;

      case "ArrowLeft":
        if (isTocLink && isExpandableLink(focused)) {
          const btn = focused.nextElementSibling;
          if (btn.getAttribute("aria-expanded") === "true") {
            e.preventDefault();
            btn.click();
          }
        }
        break;

      case " ":
        // Space does not fire a native click on <a> — handle explicitly.
        if (isTocLink && isExpandableLink(focused)) {
          e.preventDefault();
          focused.nextElementSibling.click();
        }
        // Space on a button is already handled natively (fires click).
        break;
    }
  });
};

/**
 * The links in the TOC has ids which corresponds to the ids of the titles in the report.
 * To differentiate them, the links in the TOC follows the convention: 'toc-{id}'.
 * This utility function simply extracts the id-part, which is used for comparrisons.
 */
const getIdFromTocId = (id) => {
  if (id.startsWith("toc-")) {
    return id.substring(4); // Remove "toc-" prefix
  }
  return id;
};
